import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import ProvidersEditModal from './ProvidersEditModal';

const columnHelper = createColumnHelper();

export default function ProvidersTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  handleUpdateProvider,
  sorting,
  onSortingChange,
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData],
  );

  const [editModalState, setEditModalState] = React.useState({ isOpen: false });
  const [selectedProvider, setSelectedProvider] = React.useState(null);

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
    manualSorting: false,
  });


  const openEditModal = (rowData) => {
    setSelectedProvider(rowData);
    setEditModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setEditModalState({ isOpen: false });
    setSelectedProvider(null);
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => (
          <Flex align="center">
            <Text fontSize="sm" fontWeight="normal">
              {info.getValue()}
            </Text>
            {col.actions &&
              col.actions.map((action, index) => (
                <IconButton
                  key={index}
                  aria-label={action.label}
                  icon={action.icon}
                  size="sm"
                  variant="ghost"
                  colorScheme={action.colorScheme}
                  ml={2}
                  onClick={() => {
                    if (action.type === 'edit') {
                      openEditModal(info.row.original);
                    }
                  }}
                />
              ))}
          </Flex>
        ),
      }),
    );
  }

  return (
    <>
      <PageTable
        flexDirection="column"
        w="100%"
        px="0px"
       >
        <Box>
          <PageTable.Scroll>
            <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                        <Thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <SortableTableHeader
                                  key={header.id}
                                  header={header}
                                  borderColor={borderColor}
                                />
                              ))}
                            </Tr>
                          ))}
                        </Thead>
                        <Tbody>
                          {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
          </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </Box>
      </PageTable>
      <ProvidersEditModal
        isOpen={editModalState.isOpen}
        onClose={closeEditModal}
        provider={selectedProvider}
        onUpdate={handleUpdateProvider}
      />
    </>
  );
}
